import { supabase } from './supabaseClient';
import { supabaseUrl, supabaseAnonKey } from './supabaseClient';

export type ConfidenceLevel = 'low' | 'medium' | 'high';
export type UnderstandingState = 'understood' | 'partially_understood' | 'misconception' | 'needs_reinforcement';

interface LogMasteryEventParams {
  studentId: string;
  conceptId: number;
  eventType: string;
  correct?: boolean | null;
  confidence?: ConfidenceLevel | null;
  interactionStartedAt?: Date | null;
  firstResponseAt?: Date | null;
  finalSubmissionAt?: Date | null;
  revisionsCount?: number;
  difficulty?: string | null;
  mistakeCategory?: string | null;
}

export async function logMasteryEvent({
  studentId,
  conceptId,
  eventType,
  correct = null,
  confidence = null,
  interactionStartedAt = null,
  firstResponseAt = null,
  finalSubmissionAt = null,
  revisionsCount = 0,
  difficulty = null,
  mistakeCategory = null,
}: LogMasteryEventParams): Promise<void> {
  try {
    const { error } = await supabase.rpc('log_mastery_event', {
      p_student_id: studentId,
      p_concept_id: conceptId,
      p_event_type: eventType,
      p_correct: correct,
      p_confidence: confidence,
      p_interaction_started_at: (interactionStartedAt ?? new Date()).toISOString(),
      p_first_response_at: firstResponseAt?.toISOString() ?? null,
      p_final_submission_at: (finalSubmissionAt ?? new Date()).toISOString(),
      p_revisions_count: revisionsCount,
      p_difficulty: difficulty,
      p_mistake_category: mistakeCategory ?? null,
    });
    if (error) {
      console.warn('[mastery] logMasteryEvent:', error.message);
    }
  } catch {
    // Non-critical — mastery events must never break the learning flow
  }
}

export async function recordMisconception({
  studentId,
  conceptId,
  misconceptionType,
  corrected = false,
}: {
  studentId: string;
  conceptId: number;
  misconceptionType: string;
  corrected?: boolean;
}): Promise<void> {
  try {
    const { error } = await supabase.rpc('record_misconception', {
      p_student_id: studentId,
      p_concept_id: conceptId,
      p_misconception_type: misconceptionType.substring(0, 100),
      p_corrected: corrected,
    });
    if (error) console.warn('[mastery] recordMisconception:', error.message);
  } catch {
    // Non-critical
  }
}

export async function isMisconceptionResolved(
  studentId: string,
  conceptId: number,
  misconceptionType: string,
): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('is_misconception_resolved', {
      p_student_id: studentId,
      p_concept_id: conceptId,
      p_misconception_type: misconceptionType.substring(0, 100),
    });
    if (error) return false;
    return data as boolean;
  } catch {
    return false;
  }
}

export async function getMasteryForConcepts(
  studentId: string,
  conceptIds: number[],
): Promise<Record<number, string>> {
  if (!conceptIds.length) return {};
  try {
    const { data, error } = await supabase.rpc('get_mastery_for_concepts', {
      p_student_id: studentId,
      p_concept_ids: conceptIds,
    });
    if (error || !data) return {};
    const map: Record<number, string> = {};
    for (const row of data as { concept_id: number; label: string }[]) {
      map[row.concept_id] = row.label;
    }
    return map;
  } catch {
    return {};
  }
}

export async function getMasteryStatsForConcepts(
  studentId: string,
  conceptIds: number[],
): Promise<Record<number, { count: number; lastDate: string | null }>> {
  if (!conceptIds.length) return {};
  try {
    const { data, error } = await supabase
      .from('mastery_events')
      .select('concept_id, created_at')
      .eq('student_id', studentId)
      .in('concept_id', conceptIds);
    if (error || !data) return {};
    const stats: Record<number, { count: number; lastDate: string | null }> = {};
    for (const row of data as { concept_id: number; created_at: string }[]) {
      const id = row.concept_id;
      if (!stats[id]) stats[id] = { count: 0, lastDate: null };
      stats[id].count++;
      if (!stats[id].lastDate || row.created_at > stats[id].lastDate!) {
        stats[id].lastDate = row.created_at;
      }
    }
    return stats;
  } catch {
    return {};
  }
}

export interface StudentConceptState {
  concept_id: number;
  concept_name: string;
  subject_name: string;
  concept_state: string;
  mastery_score: number;
  phase_count: number;
  has_micro: boolean;
  has_simulate: boolean;
  has_apply: boolean;
  has_exit: boolean;
  last_interaction_at: string;
  micro_correct: boolean | null;
  simulate_correct: boolean | null;
  micro_attempts: number;
  simulate_attempts: number;
  apply_attempts: number;
  overconfident: boolean;
  underconfident: boolean;
  is_decaying: boolean;
  apply_max_difficulty: string | null;
  avg_revisions: number;
  phase_abandoned: boolean;
  confidence_low: number;
  confidence_medium: number;
  confidence_high: number;
}

export async function getStudentConceptState(studentId: string): Promise<StudentConceptState[]> {
  try {
    const { data, error } = await supabase.rpc('get_student_concept_state', { p_student_id: studentId });
    if (error) throw error;
    return (data || []) as StudentConceptState[];
  } catch {
    return [];
  }
}

export async function getPhaseAttemptCounts(
  studentId: string,
  conceptId: number,
): Promise<{ predict: number; reflect: number }> {
  try {
    const { data, error } = await supabase.rpc('get_phase_attempt_counts', {
      p_student_id: studentId,
      p_concept_id: conceptId,
    });
    if (error || !data) return { predict: 0, reflect: 0 };
    return { predict: (data as any).predict ?? 0, reflect: (data as any).reflect ?? 0 };
  } catch {
    return { predict: 0, reflect: 0 };
  }
}

export async function evaluateUnderstanding(params: {
  conceptName: string;
  context: 'exit_ticket' | 'remediation';
  question: string;
  studentAnswer: string;
  correctAnswer?: string;
}): Promise<{ state: UnderstandingState; feedback: string } | null> {
  try {
    const response = await fetch(`${supabaseUrl}/functions/v1/evaluate-understanding`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify(params),
    });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}
